import { Template } from "@/types";
import SystemBadge from "./SystemBadge";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import TemplatePreviewButton from "./TemplatePreviewButton";
import { useTranslation } from "react-i18next";

interface TemplateListItemProps {
  template: Template;
  onPreviewClick?: (template: Template) => void;
}

const TemplateListItem = ({ template, onPreviewClick }: TemplateListItemProps) => {
  const { t, i18n } = useTranslation();
  const isSystemTemplate = template.isSystem || template.is_system;
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // Определяем мобильное устройство
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/templates/edit/${template.id}`);
  };

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreviewClick?.(template);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card
      className={`overflow-hidden transition-all duration-300 group border border-border/50 ${isMobile
          ? 'active:scale-[0.98] active:shadow-sm'
          : 'hover:shadow-lg hover:scale-[1.02] cursor-pointer hover:border-brand-200'
        }`}
      onClick={handleOpen}
    >
      <div className="relative">
        <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
          {/* Header with title and badge */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className={`font-medium truncate text-foreground mb-1 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {template.name}
              </h3>

              {/* Type badge */}
              <div className="flex items-center gap-2">
                {isSystemTemplate ? (
                  <SystemBadge />
                ) : (
                  <span className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs px-3 py-1 rounded-full font-medium border border-emerald-200 dark:border-emerald-700">
                    {t('templates.list_item.custom_badge')}
                  </span>
                )}
              </div>
            </div>

            {/* Preview button - всегда виден */}
            {onPreviewClick && (
              <div className="flex items-center gap-2">
                <TemplatePreviewButton
                  template={template}
                  onClick={handlePreviewClick}
                  variant="ghost"
                  size={isMobile ? "default" : "sm"}
                />
              </div>
            )}
          </div>

          {/* Description */}
          <p className={`text-muted-foreground line-clamp-3 leading-relaxed ${isMobile ? 'text-xs mb-3' : 'text-sm mb-4'
            }`}>
            {template.description}
          </p>

          {/* Footer with metadata and open button */}
          <div className={`flex items-center justify-between text-muted-foreground border-t border-border ${isMobile ? 'text-xs pt-2' : 'text-xs pt-3'
            }`}>
            <div className="flex items-center gap-1">
              <Calendar size={isMobile ? 10 : 12} />
              <span>{t('templates.list_item.created_at', { date: formatDate(template.createdAt) })}</span>
            </div>

            {/* Open button - всегда видим */}
            <Button
              variant="ghost"
              size={isMobile ? "default" : "sm"}
              onClick={handleOpen}
              className={`text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 ${isMobile ? 'h-10 px-3' : 'h-8 w-8 p-0'
                }`}
            >
              {isMobile ? (
                t('templates.list_item.open_btn')
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TemplateListItem;